import { Button, Spinner } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import FavoriteStar from "components/sidebar/BusItem/FavoriteStar";
import { useFavoriteBuses } from "contexts/FavoriteBusesContext/FavoriteBusesContext";
import {
    useFavoriteScopedBusesContext,
    useFavoriteScopedBusesLoading
} from "contexts/FavoriteBusesContext/FavoriteScopedBusesContext";
import { useMapViewRoutingMetadata } from "contexts/RoutingMetadataContext";
import React from "react";
import { Virtuoso } from "react-virtuoso";
import "./FavoritesPanel.scss";
import {
    FAVORITES_PANEL_EMPTY_NO_RESULTS,
    FAVORITES_PANEL_LIST,
    FAVORITES_PANEL_LOADING,
    getFavoritesPanelView,
    getUnresolvedFavorites,
    getUnresolvedFavoritesLabel
} from "./favoritesPanelHelpers";
import FavoritesPanelRow from "./FavoritesPanelRow";

const HEADER_STAR_SIZE = 14;
const EMPTY_STATE_STAR_SIZE = 28;
const SWITCH_HINT = "Switch scope or generator type to see them.";

// Lists every favorite in the region, ignoring all filters and the viewport,
// but displays accurate project size if it fits in cost filters.
// Opening or closing it never changes what is filtered.
const FavoritesPanel: React.FC = () => {
    const { region } = useMapViewRoutingMetadata();
    const { favorites, setFavoritesPanelOpen } = useFavoriteBuses();
    // Fetched by id, so favorites outside the viewport still render in full.
    const favoriteScopedBuses = useFavoriteScopedBusesContext();
    const isLoading = useFavoriteScopedBusesLoading();
    // The count is the saved list; the rows are what this view can resolve, and
    // the two legitimately differ. See favoritesPanelHelpers.
    const view = getFavoritesPanelView(
        favorites.length,
        favoriteScopedBuses.length,
        isLoading
    );
    // Hidden while loading: the rows are the previous result then, which lacks
    // anything starred since, so the gap would be wrong.
    const unresolvedCount = isLoading
        ? 0
        : getUnresolvedFavorites(favorites, favoriteScopedBuses).length;

    return (
        <div className="FavoritesPanel">
            <div className="FavoritesPanel-header">
                <Button
                    variant="minimal"
                    icon={IconNames.CHEVRON_LEFT}
                    aria-label="Back to filters"
                    onClick={() => setFavoritesPanelOpen(false)}
                />
                <FavoriteStar
                    size={HEADER_STAR_SIZE}
                    filled={true}
                    className="FavoritesPanel-header-star"
                />
                <span className="FavoritesPanel-header-title">
                    All favorites
                </span>
                <span className="FavoritesPanel-header-count bp5-text-small bp5-text-muted">
                    {favorites.length}
                </span>
            </div>

            {view === FAVORITES_PANEL_LIST ? (
                <>
                    <Virtuoso
                        className="FavoritesPanel-list"
                        data={favoriteScopedBuses}
                        computeItemKey={(index, scopedBus) =>
                            scopedBus.bus.niraBusId
                        }
                        itemContent={(index, scopedBus) => (
                            <FavoritesPanelRow scopedBus={scopedBus} />
                        )}
                    />
                    {unresolvedCount > 0 && (
                        <div className="FavoritesPanel-note bp5-text-small bp5-text-muted">
                            {`${getUnresolvedFavoritesLabel(
                                unresolvedCount
                            )}. ${SWITCH_HINT}`}
                        </div>
                    )}
                </>
            ) : view === FAVORITES_PANEL_LOADING ? (
                <div className="FavoritesPanel-empty">
                    <Spinner />
                </div>
            ) : (
                <div className="FavoritesPanel-empty">
                    <FavoriteStar
                        size={EMPTY_STATE_STAR_SIZE}
                        filled={false}
                        className="FavoritesPanel-empty-star"
                    />
                    <h3 className="bp5-heading">
                        {view === FAVORITES_PANEL_EMPTY_NO_RESULTS
                            ? "No results in this view"
                            : `No favorites in ${region} yet`}
                    </h3>
                    <div className="FavoritesPanel-empty-body bp5-text-muted">
                        {view === FAVORITES_PANEL_EMPTY_NO_RESULTS
                            ? `${getUnresolvedFavoritesLabel(
                                  favorites.length
                              )}. They are still saved. ${SWITCH_HINT}`
                            : "Star a bus from its detail pane to add it to the favorites list."}
                    </div>
                </div>
            )}
        </div>
    );
};

export default FavoritesPanel;
